import type { ErrorType } from "@/composables/utils/useErrorBoundary";

/**
 * Configuration for rendering a classified error inside a dialog.
 *
 * Maps each ErrorType to an icon, colour, i18n key suffixes for title/subtitle,
 * and the action button type (retry vs. login).
 */
export interface DialogErrorConfig {
  icon: string;
  iconColor: string;
  titleKey: string;
  subtitleKey: string;
  actionType: "retry" | "login" | "clear";
  actionIcon: string;
}

/**
 * Error UI configuration keyed by ErrorType.
 *
 * Icon and colour choices match AllVisitsTable error states for visual consistency.
 * The titleKey/subtitleKey are suffixes appended to a base i18n namespace by the
 * consuming component (e.g. `pages.all-visits.dialogs.errors.${titleKey}`).
 */
export const DIALOG_ERROR_CONFIG: Record<string, DialogErrorConfig> = {
  server: {
    icon: "mdi-server-off",
    iconColor: "error",
    titleKey: "serverTitle",
    subtitleKey: "serverSubtitle",
    actionType: "retry",
    actionIcon: "mdi-refresh",
  },
  network: {
    icon: "mdi-wifi-off",
    iconColor: "warning",
    titleKey: "networkTitle",
    subtitleKey: "networkSubtitle",
    actionType: "retry",
    actionIcon: "mdi-refresh",
  },
  timeout: {
    icon: "mdi-clock-alert-outline",
    iconColor: "warning",
    titleKey: "timeoutTitle",
    subtitleKey: "timeoutSubtitle",
    actionType: "retry",
    actionIcon: "mdi-refresh",
  },
  unknown: {
    icon: "mdi-alert-outline",
    iconColor: "error",
    titleKey: "unknownTitle",
    subtitleKey: "unknownSubtitle",
    actionType: "retry",
    actionIcon: "mdi-refresh",
  },
  validation: {
    icon: "mdi-alert-circle-outline",
    iconColor: "warning",
    titleKey: "validationTitle",
    subtitleKey: "validationSubtitle",
    actionType: "clear",
    actionIcon: "mdi-filter-remove-outline",
  },
};

/**
 * Resolve the error UI configuration for a given ErrorType.
 *
 * Falls back to the "unknown" configuration for unrecognised types.
 *
 * @param errorType - Classified error type from useErrorBoundary
 * @returns The icon, colour, and i18n key config for the error
 */
export function getDialogErrorConfig(errorType: ErrorType | string): DialogErrorConfig {
  return DIALOG_ERROR_CONFIG[errorType] ?? DIALOG_ERROR_CONFIG["unknown"]!;
}
